import {
    Alert,
    Button,
    Classes,
    Intent,
    OverlayToaster,
    Position,
    Tag
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { CardStack } from "components/common/CardStack";
import { FieldLabel } from "components/common/FieldLabel";
import { FileChip } from "components/common/FileChip";
import { NiraDialog } from "components/common/NiraDialog";
import { Surface } from "components/common/Surface";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { LOADING } from "graphql/helpers/queryHelpers";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    CustomUpgrade,
    useCustomUpgrades
} from "in_queue/contexts/CustomUpgradesContext";
import { useSubscriptionForCluster } from "in_queue/contexts/ProjectDataContext";
import { getRelevantModelSet } from "in_queue/utils/customUpgradeValidation";
import { capitalize } from "lodash";
import { DateTime } from "luxon";
import { useState } from "react";
import { ADMIN_TYPE } from "types/roleType";
import {
    CUSTOM_UPGRADE_STATUS_ACTIVE,
    CUSTOM_UPGRADE_STATUS_ARCHIVED
} from "./customUpgradeConstants";
import css from "./CustomUpgradeDialog.module.scss";
import { useArchiveCustomUpgrade } from "./useArchiveCustomUpgrade";
import { useRequestCustomUpgradeValidation } from "./useRequestCustomUpgradeValidation";
import { ValidationBanner } from "./ValidationBanner";

const NOT_PROVIDED_LABEL = "Not provided";

const CustomUpgradeToaster = OverlayToaster.create({
    position: Position.TOP
});

interface CustomUpgradeDialogProps {
    readonly upgrade: CustomUpgrade;
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly showArchiveButton?: boolean;
}

const VARIANT_FALLBACK_LABEL = "Default";

const formatOptional = (value: string | null | undefined): string =>
    value === undefined || value === null || value.trim().length === 0
        ? NOT_PROVIDED_LABEL
        : value;

const FileTypeSection: React.FC<{ upgrade: CustomUpgrade }> = ({ upgrade }) => {
    const uniqueFileFormats = new Set<string>();
    upgrade.files.forEach((file) => {
        uniqueFileFormats.add(file.sourceFileFormat);
    });
    return (
        <div>
            <FieldLabel>Format</FieldLabel>
            <span className={Classes.MONOSPACE_TEXT}>
                {Array.from(uniqueFileFormats)
                    .map((format) => `.${format}`)
                    .join(" / ")}
            </span>
        </div>
    );
};

const SourceFilesSection: React.FC<{ readonly upgrade: CustomUpgrade }> = ({
    upgrade
}) => {
    if (!upgrade.isModelSpecific) {
        const file = upgrade.files[0];
        return (
            <div>
                <FieldLabel>Source File</FieldLabel>
                <FileChip filename={file.sourceFileName} bordered />
            </div>
        );
    }

    return (
        <div>
            <FieldLabel>
                Source Files{" "}
                {upgrade.files.length > 1 && (
                    <Tag minimal round>
                        {upgrade.files.length}
                    </Tag>
                )}
            </FieldLabel>
            <CardStack>
                {upgrade.files.map((file) => (
                    <FileChip
                        key={file.id}
                        round={false}
                        filename={file.sourceFileName}
                        description={
                            file.variant?.description ?? VARIANT_FALLBACK_LABEL
                        }
                    />
                ))}
            </CardStack>
        </div>
    );
};

export const CustomUpgradeDialog: React.FC<CustomUpgradeDialogProps> = ({
    upgrade: upgradeProp,
    isOpen,
    onClose,
    showArchiveButton = true
}) => {
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const { archiveUpgrade, submitting: archiving } = useArchiveCustomUpgrade();
    const { requestValidation, submitting: revalidating } =
        useRequestCustomUpgradeValidation();
    const { role } = useUserDataContext();
    const cluster = useCluster();
    const subscription = useSubscriptionForCluster();
    const customUpgrades = useCustomUpgrades();

    const upgrade =
        (customUpgrades === "loading"
            ? undefined
            : customUpgrades.find((u) => u.id === upgradeProp.id)) ??
        upgradeProp;

    const canArchive =
        upgrade.status === CUSTOM_UPGRADE_STATUS_ACTIVE && !upgrade.isShared;
    const statusIntent =
        upgrade.status === CUSTOM_UPGRADE_STATUS_ARCHIVED
            ? Intent.WARNING
            : Intent.SUCCESS;

    const showRevalidate = role === ADMIN_TYPE && !upgrade.isShared;

    const onRevalidate = async () => {
        const result = await requestValidation(upgrade.id, "revalidate");
        CustomUpgradeToaster.show(
            result.ok
                ? {
                      intent: Intent.PRIMARY,
                      message: `Validation requested for ${upgrade.name}.`
                  }
                : { intent: Intent.DANGER, message: result.message }
        );
    };

    const onConfirmArchive = async () => {
        const result = await archiveUpgrade(upgrade.id);

        if (!result.ok) {
            CustomUpgradeToaster.show({
                intent: Intent.DANGER,
                message: result.message
            });
            return;
        }

        CustomUpgradeToaster.show({
            intent: Intent.SUCCESS,
            message: `${upgrade.name} archived.`
        });
        setIsAlertOpen(false);
        onClose();
    };

    return (
        <>
            <NiraDialog
                className={css["CustomUpgradeDialog"]}
                gap="large"
                isOpen={isOpen}
                onClose={onClose}
                eyebrow="Upgrade Details"
                title={upgrade.name}
                footerActions={
                    <Button text="Close" onClick={onClose} size="large" />
                }
                footerLeft={
                    canArchive &&
                    showArchiveButton && (
                        <Button
                            size="large"
                            variant="minimal"
                            text="Archive"
                            icon={IconNames.BOX}
                            onClick={() => setIsAlertOpen(true)}
                            className={css["CustomUpgradeDialog-archive"]}
                        />
                    )
                }
            >
                <ValidationBanner
                    info={upgrade.modelSetValidationInfo}
                    modelSet={getRelevantModelSet(
                        cluster,
                        subscription === LOADING ? undefined : subscription
                    )}
                    modelSets={cluster.allModelSets}
                    fileCount={upgrade.files.length}
                    canValidate={showRevalidate}
                    onValidate={onRevalidate}
                    validating={revalidating}
                />
                <div className={css["CustomUpgradeDialog-grid"]}>
                    <div>
                        <FieldLabel>Status</FieldLabel>
                        <Tag minimal round intent={statusIntent}>
                            <strong>{capitalize(upgrade.status)}</strong>
                        </Tag>
                    </div>
                    <FileTypeSection upgrade={upgrade} />
                    <div>
                        <FieldLabel>Created</FieldLabel>
                        {DateTime.fromJSDate(upgrade.createdAt).toLocaleString(
                            DateTime.DATE_FULL
                        )}
                    </div>
                </div>

                <SourceFilesSection upgrade={upgrade} />

                <div>
                    <FieldLabel>Notes</FieldLabel>
                    <Surface tone="tint" bordered radius="md" padding="small">
                        {formatOptional(upgrade.notes)}
                    </Surface>
                </div>
            </NiraDialog>
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Archive upgrade"
                icon={IconNames.ARCHIVE}
                intent={Intent.DANGER}
                isOpen={isAlertOpen}
                loading={archiving}
                onCancel={() => setIsAlertOpen(false)}
                onConfirm={onConfirmArchive}
            >
                <p>
                    Are you sure you want to archive "{upgrade.name}"? This
                    action cannot be undone.
                </p>
            </Alert>
        </>
    );
};
